import { KPI } from '@/components/ui/KPI';
import { SectionHead } from '@/components/ui/SectionHead';
import { SeriesBars } from '@/components/SeriesBars';
import { TimeByBrandList } from '@/components/TimeByBrandList';
import type { EngagementSection } from '@/api/worldCup';

// Format an ISO date (YYYY-MM-DD) as "13 Jun 2026" for the tracking note.
// Parsed as UTC so the day never shifts across timezones; falls back to the
// raw string if it isn't a parseable date.
function fmtTrackedSince(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function EngagementView({ section }: { section: EngagementSection }) {
  const time = section.time_in_app;
  const timeKpiCount = (time?.total ? 1 : 0) + (time?.avg_per_session ? 1 : 0);
  const hasBrandAvg = time?.by_brand?.some((b) => b.avg) ?? false;
  // Tracking-start date from the payload; fall back to 13 Jun 2026 for older
  // FR/ES payloads generated before `tracked_since` was added.
  const trackedSince = time?.tracked_since ? fmtTrackedSince(time.tracked_since) : '13 Jun 2026';
  return (
    <div>
      <SectionHead tag="user engagement">Engagement at a glance</SectionHead>
      <div className={section.kpis.unique_players ? 'grid-3' : 'grid-2'}>
        <KPI label="Unique opt-ins" value={section.kpis.unique_optins.display} accent="engage" foot={section.kpis.unique_optins.foot} />
        <KPI label="Unique visitors" value={section.kpis.unique_visitors.display} accent="engage" foot={section.kpis.unique_visitors.foot} />
        {section.kpis.unique_players && (
          <KPI label="Unique players" value={section.kpis.unique_players.display} accent="engage" foot={section.kpis.unique_players.foot} />
        )}
      </div>

      <SectionHead tag="trends">Trends</SectionHead>
      <div className="grid-2">
        <SeriesBars weekly={section.weekly_unique_visitors} daily={section.daily_unique_visitors} color="var(--accent)" />
        <SeriesBars weekly={section.weekly_games_played} daily={section.daily_games_played} />
      </div>

      {/* Time in app — kept at the bottom of the page. */}
      {time && (time.total || time.avg_per_session || (time.by_brand?.length ?? 0) > 0) && (
        <>
          <SectionHead tag="time in app">Time in app</SectionHead>
          <p className="section-note">
            Time has been tracked since {trackedSince}, so totals cover the period from then onward.
            {time.avg_per_session && (
              <> A session is a stretch of activity with no gap longer than 15&nbsp;minutes; average session duration is the mean length of those sessions.</>
            )}
            {hasBrandAvg && <> Per brand, the average is the mean time spent on that brand per visit.</>}
          </p>
          {(time.total || time.avg_per_session) && (
            <div className={timeKpiCount > 1 ? 'grid-2' : 'grid-1'} style={{ marginBottom: 'var(--gap-kpi)' }}>
              {time.total && <KPI label="Total time in app" value={time.total.display} accent="engage" foot="all screens, incl. home" />}
              {time.avg_per_session && <KPI label="Avg session duration" value={time.avg_per_session.display} accent="engage" foot="mean per session" />}
            </div>
          )}
          {time.by_brand && time.by_brand.length > 0 && <TimeByBrandList items={time.by_brand} />}
        </>
      )}
    </div>
  );
}
